import { CHANNELS } from '@/models/dashboards/channels.enum';
import { QUERY_TIMEFRAME } from '@/models/dashboards/metrics.constants';

const ROLES = Object.freeze({
  EDITOR: {
    value: 'EDITOR',
    icon: 'edit',
    tooltipText: 'Can manage reports, users and permissions.',
  },
  VIEWER: {
    value: 'VIEWER',
    icon: 'eye',
    tooltipText: 'Can view reports and change reporting periods.',
  },
  OWNER: {
    value: 'OWNER',
  },
});

export const BREAKDOWN_TYPE = Object.freeze({
  ASSIGNEE: 'assignee',
  BOARD: 'board',
  CAMPAIGN: 'campaign',
  COMPETITOR: 'competitor',
  INDUSTRY: 'industry',
  MEDIA_TYPE: 'media type',
  MENTION_TYPE: 'mention type',
  MESSAGE_TAG: 'message tag',
  MESSAGE_TYPE: 'message type',
  TOPIC: 'topic',
});

export const WARN_REMOVING_GOAL_FROM_CONTENT_REPORT_BANNER = {
  message: 'Changing this metric will remove the goal',
  severity: 'warn',
};

export const NO_CREATORS_AVAILABLE_TOOLTIP =
  'No creators added yet. Start adding to Your Creators to unlock this option.';

export const CONTENT_REPORT_CLEAR_GOAL_WARNING_DIALOG = {
  group: 'global',
  header: 'Change Metric and remove goal',
  message:
    'Changing this sorting metric will remove the goal from this content report. Are you sure you want to change it?',
  acceptLabel: 'Change Metric',
  acceptSeverity: 'danger',
};

export const METRIC_TYPES = {
  PLATFORM: 'platformMetrics',
  CUSTOM: 'customMetrics',
};

export const REPORT_FORM_BANNER_MESSAGES = {
  CREATOR_CHANNELS: "Some channels don't have any creators yet:",
  CREATOR_SOURCE_WARNING:
    "You haven't added any creators yet. Add them in Your Creators to start reporting on performance.",
  NOT_CONNECTED_CREATORS_WARNING:
    'To access this option, select creators who are connected to your brand through Dash Social',
  SOME_CONNECTED_CREATORS_ONLY_WARNING:
    'Please note that only data from connected creators will be used in this report',
  SOCIAL_LISTENING_CHANNELS_INFO:
    'Channel filters applied to your topics in Social Listening are not applied to topics in Dashboards.',
  IG_LIVE_API_WARNING: 'This Metric can only show data up to a max of 30 days',
  CONNECT_TIKTOK_ADS: 'Connect your TikTok Ads account for promoted post and ad insights',
  CONNECT_META_ADS: 'Connect your Meta Ads account for promoted post and ad insights',
  RECONNECT_TIKTOK_ADS: 'Re-connect your TikTok Ad Account to enable this metric',
  RECONNECT_META_ADS: 'Re-connect your Meta Ad Account to enable this metric',
};

export const NON_ADDITIVE_METRIC_RESTRICTIONS = {
  TOTAL_REQUIRES_SINGLE:
    'This metric measures unique values that cannot be summed across different brands or channels. To select an Aggregate Total, please select a single brand and channel.',
  MULTI_BRAND_REQUIRES_BRAND_BREAKDOWN:
    'This metric cannot be combined across brands because it measures unique counts. Please use a brand breakdown to view these results accurately.',
  MULTI_CHANNEL_REQUIRES_CHANNEL_BREAKDOWN:
    'This metric cannot be summed across channels because it is unique to each platform. Please use a channel breakdown to view these results accurately.',
};

export const DISCONTINUED_METRIC_WARNING_MESSAGE =
  'The metric in this report has been discontinued. Please select a new metric to edit this report.';

export const REQUEST_FAILED_BANNER_MESSAGE = 'Failed to fetch data. Please try again later';

export const REPORT_CREATION_FAILED_MESSAGE = 'Report Creation Failed';

export const REPORT_TYPE_LABELS = {
  METRIC: 'Metric Report',
  GRAPH: 'Graph Report',
  CONTENT: 'Content Report',
};

export const BRAND_TABS = {
  BRANDS: 'brands',
  BRAND_TAGS: 'brandTags',
};

export const CREATOR_TABS = {
  CREATORS: 'creators',
  CREATOR_TAGS: 'creatorTags',
};

export const COMPETITOR_TABS = {
  COMPETITORS: 'competitors',
  COMPETITOR_TAGS: 'competitorTags',
};

export const CHANNEL_TABS = {
  CHANNELS: 'channels',
};

export const CAMPAIGN_STATUS_TABS = Object.freeze({
  [CHANNELS.META_ADS.value]: {
    ALL: 'ALL',
    ACTIVE: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
    PAUSED: 'PAUSED',
    WITH_ISSUES: 'WITH_ISSUES',
  },
  [CHANNELS.TIKTOK_ADS.value]: {
    ALL: 'ALL',
    ENABLE: 'ENABLE',
    DELETED: 'DELETED',
    DISABLED: 'DISABLED',
    OVER_BUDGET: 'BUDGET_EXCEED',
  },
});

export const SORT_ORDER_OPTIONS = Object.freeze([
  {
    value: 'DESC',
    label: 'Top Performing Posts',
    ugcText: 'UGC Top Performing Posts',
    storiesText: 'Top Performing Stories',
    creatorText: 'Top Performing Creator Content',
  },
  {
    value: 'ASC',
    label: 'Lowest Performing Posts',
    storiesText: 'Lowest Performing Stories',
    creatorText: 'Lowest Performing Creator Content',
  },
]);

export const DATE_PUBLISHED_SORT_ORDER_OPTIONS = Object.freeze([
  {
    value: 'DESC',
    label: 'Recently Published',
  },
  {
    value: 'ASC',
    label: 'Earliest Published',
  },
]);

export const DISPLAY_STRATEGY = Object.freeze({
  DEFAULT_VALUE: 'DEFAULT_VALUE',
  PERCENT_OF_GRAND_TOTAL: 'PERCENT_OF_GRAND_TOTAL',
});

export const DISPLAY_STRATEGY_OPTIONS = Object.freeze([
  {
    value: DISPLAY_STRATEGY.DEFAULT_VALUE,
    label: 'Display as Total',
    tooltip: 'The metric value shown for each row (e.g., total messages received by a channel)',
    disableOptionByAggregateBy: ['AGE', 'GENDER'],
  },
  {
    value: DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL,
    label: 'Display as % of Total',
    tooltip:
      "The percentage each row contributes to the table total (e.g., a channel's share of total messages received)",
    suffix: 'as % of Total',
    topicLabel: 'Share of Voice by',
  },
]);
export const ALL_CONTENT = 'ALL_CONTENT';
export const SELECT_TAGS = 'SELECT_TAGS';
export const UGC = 'UGC';
export const COMMUNITY = 'COMMUNITY';
export const LISTENING = 'LISTENING';
export const CREATORS = 'CREATORS';
export const IG_LIVE_API = 'IG_LIVE_API';

export const REPORT_PRODUCT_CATEGORIES = Object.freeze({
  OWNED: 'OWNED',
  COMPETITIVE: 'COMPETITIVE',
  AD: 'AD',
  INDUSTRY: 'INDUSTRY',
  UGC: 'UGC',
  CREATOR: 'CREATOR',
  COMMUNITY: 'COMMUNITY',
  TSI: 'TSI',
  WIDGET: 'WIDGET',
});

export const REPORT_COMMUNITY_FILTERS = Object.freeze({
  MESSAGE_TAG_IDS: 'message_tag_ids',
  MESSAGE_TYPES: 'message_types',
  SENTIMENT: 'sentiment',
  ASSIGNEE_IDS: 'assignee_ids',
});

export const COMMUNITY_FILTER_CONFIG = Object.freeze({
  [REPORT_COMMUNITY_FILTERS.MESSAGE_TAG_IDS]: {
    searchable: true,
    hideUntilSearch: false,
    disableSelectAll: true,
  },
  [REPORT_COMMUNITY_FILTERS.MESSAGE_TYPES]: {
    searchable: false,
    hideUntilSearch: false,
    disableSelectAll: true,
  },
  [REPORT_COMMUNITY_FILTERS.SENTIMENT]: {
    searchable: false,
    hideUntilSearch: false,
    disableSelectAll: true,
  },
  [REPORT_COMMUNITY_FILTERS.ASSIGNEE_IDS]: {
    searchable: true,
    hideUntilSearch: false,
    disableSelectAll: true,
  },
});

export const REPORT_LISTENING_FILTERS = Object.freeze({
  SENTIMENT: 'sentiment',
  MEDIA_TYPE: 'media_types',
  LOCATION: 'locations',
  MENTION_TYPE: 'mention_types',
  COMPETITOR: 'competitors',
  INDUSTRY: 'industry_ids',
});

export const ALL_REPORT_FILTERS = Object.freeze({
  ...REPORT_COMMUNITY_FILTERS,
  ...REPORT_LISTENING_FILTERS,
});

export const LISTENING_FILTER_CONFIG = Object.freeze({
  [REPORT_LISTENING_FILTERS.LOCATION]: {
    searchable: true,
    hideUntilSearch: true,
    disableSelectAll: true,
  },
  [REPORT_LISTENING_FILTERS.COMPETITOR]: {
    searchable: true,
    hideUntilSearch: false,
    disableSelectAll: false,
  },
  [REPORT_LISTENING_FILTERS.INDUSTRY]: {
    searchable: true,
    hideUntilSearch: false,
    disableSelectAll: false,
  },
  [REPORT_LISTENING_FILTERS.SENTIMENT]: {
    searchable: false,
    hideUntilSearch: false,
    disableSelectAll: false,
  },
  [REPORT_LISTENING_FILTERS.MEDIA_TYPE]: {
    searchable: false,
    hideUntilSearch: false,
    disableSelectAll: false,
  },
  [REPORT_LISTENING_FILTERS.MENTION_TYPE]: {
    searchable: false,
    hideUntilSearch: false,
    disableSelectAll: false,
  },
});

export const MESSAGE_TAG_OPTION_KEYS = Object.freeze({
  BRAND: 'brand',
  TAG: 'tag',
});

export const REPORT_PANEL_OPTIONS = Object.freeze({
  METRIC_TYPE: {
    title: 'Platform Metric or Custom Metric',
    subHeader: 'Select a metric type',
    tooltip:
      'Platform metrics are standard Dash Social metrics. Custom metrics are metrics created by your team.',
    formKey: 'metricType',
  },
  GOALS: {
    tooltip: 'Adding a goal to the report will display your progress towards the target',
    formKey: 'goals',
  },
  REPORT_TYPE: {
    title: 'Report Type',
    subHeader: 'Select the type of report',
    tooltip:
      'Reports showcase your top or bottom posts by a performance metric. Metric Reports track one metric for selected handles and channels. Graph Reports visualize your data with line or bar charts.',
    formKey: 'reportType',
  },
  LISTENING: {
    formKey: 'listening',
    TOPICS: {
      title: 'Topics',
      subHeader: 'Select Topics',
      tooltip:
        'Reports showcase your top or bottom posts by a performance metric. Metric Reports track one metric for selected handles and channels. Graph Reports visualize your data with line or bar charts.',
      TOPIC: {
        formKey: 'topic_ids',
      },
      COMPARISON: {
        formKey: 'comparison_topic_ids',
      },
      cta: 'Manage Topics',
    },
  },
  BRANDS: {
    title: 'Brands',
    tooltip: null,
    formKeys: ['selectedBrands', 'selectedBrandTags'],
    hint: 'Select one or multiple brands',
    TABS: [
      {
        title: 'Brands',
        value: BRAND_TABS.BRANDS,
        subHeader: 'Select brands to report on',
        placeholderText: 'Search brands',
        hint: 'Select one or multiple brands',
        disabledTooltip: 'Deselect all brand tags to select brands',
      },
      {
        title: 'Brand Tags',
        value: BRAND_TABS.BRAND_TAGS,
        subHeader: 'Select brand tags to report on',
        placeholderText: 'Search brand tags',
        hint: 'Select one or multiple brands',
        disabledCustomMetricsTooltip: 'Brand Tags are not available for custom metrics',
        disabledTooltip: 'Deselect all brands to select brand tags',
      },
    ],
  },
  FILTERS: {
    title: 'Filters',
    formKey: 'filters',
    COMMUNITY_FILTERS: {
      formKey: 'community',
      filters: [
        {
          title: 'Message Tag',
          pluralTitle: 'Message Tags',
          formKey: REPORT_COMMUNITY_FILTERS.MESSAGE_TAG_IDS,
        },
        {
          title: 'Message Type',
          pluralTitle: 'Message Types',
          formKey: REPORT_COMMUNITY_FILTERS.MESSAGE_TYPES,
        },
        {
          title: 'Sentiment',
          pluralTitle: 'Sentiment',
          formKey: REPORT_COMMUNITY_FILTERS.SENTIMENT,
        },
        {
          title: 'Assignee',
          pluralTitle: 'Assignees',
          formKey: REPORT_COMMUNITY_FILTERS.ASSIGNEE_IDS,
          dropdown: {
            title: 'Assignee',
          },
        },
      ],
    },
    LISTENING_FILTERS: {
      formKey: 'listening',
      filters: [
        {
          title: 'Sentiment',
          pluralTitle: 'Sentiment',
          formKey: REPORT_LISTENING_FILTERS.SENTIMENT,
        },
        {
          title: 'Media Type',
          pluralTitle: 'Media Types',
          formKey: REPORT_LISTENING_FILTERS.MEDIA_TYPE,
        },
        {
          title: 'Location',
          pluralTitle: 'Locations',
          formKey: REPORT_LISTENING_FILTERS.LOCATION,
          dropdown: {
            eagerMount: true,
          },
        },
        {
          title: 'Mention Type',
          pluralTitle: 'Mention Types',
          formKey: REPORT_LISTENING_FILTERS.MENTION_TYPE,
        },
        {
          title: 'Competitor',
          pluralTitle: 'Competitors',
          formKey: REPORT_LISTENING_FILTERS.COMPETITOR,
        },
        {
          title: 'Industry',
          pluralTitle: 'Industries',
          formKey: REPORT_LISTENING_FILTERS.INDUSTRY,
        },
      ],
    },
  },
  EARNED_SOURCE: {
    title: 'Source',
    sectionDescription: 'Select Creators',
    formKey: 'earnedSource',
    TABS: [
      {
        title: 'Creators',
        value: CREATOR_TABS.CREATORS,
        subHeader: 'Select Creators',
        placeholderText: 'Search Creators',
        hint: 'Select one or multiple creators',
        formKey: CREATOR_TABS.CREATORS,
      },
      {
        title: 'Creator Tags',
        value: CREATOR_TABS.CREATOR_TAGS,
        subHeader: 'Select creator tags',
        placeholderText: 'Search creator tags',
        hint: 'Select one or multiple creators',
        formKey: CREATOR_TABS.CREATOR_TAGS,
      },
    ],
    SELECT_FIELD: {
      subHeader: 'Choose to report on UGC or to select specific creators',
      formKey: 'earnedSourceType',
      SelectOptions: [
        { label: 'UGC', value: UGC },
        { label: 'Creators', value: CREATORS },
      ],
    },
  },
  COMPETITORS: {
    title: 'Competitors',
    tooltip: null,
    formKeys: ['selectedCompetitors', 'selectedCompetitorTags'],
    TABS: [
      {
        title: 'Competitors',
        value: COMPETITOR_TABS.COMPETITORS,
        subHeader: 'Select competitors to report on',
        placeholderText: 'Search competitors',
        hint: 'Select one or multiple competitors',
        disabledTooltip: 'Deselect all competitor tags to select competitors',
      },
      {
        title: 'Competitor Tags',
        value: COMPETITOR_TABS.COMPETITOR_TAGS,
        subHeader: 'Select competitor tags to report on',
        placeholderText: 'Search competitor tags',
        hint: 'Select one or multiple competitors',
        disabledTooltip: 'Deselect all competitors to select competitor tags',
      },
    ],
  },
  INDUSTRIES: {
    title: 'Industry Benchmark',
    subHeader: 'Benchmarks',
    formKey: 'benchmarks',
    tooltip: null,
    placeholderText: 'Select Industry',
  },
  BRANDS_AND_COMPETITORS: {
    title: 'Brands and/or Competitors',
    tooltip: null,
    formKeys: [
      'selectedBrands',
      'selectedBrandTags',
      'selectedCompetitors',
      'selectedCompetitorTags',
    ],
  },
  CHANNELS: {
    subHeader: 'Select social media channels',
    title: 'Channels',
    tooltip: null,
    formKey: 'channels',
    placeholderText: {
      multiple: 'Search channels',
      single: 'Search channel',
    },
  },
  PLATFORM_METRICS: {
    label: 'Select a metric type to report on',
    hint: 'Metrics shown are determined by the channels you select',
    placeholder: 'Select a metric',
    title: 'Metrics',
    formKey: 'metrics',
    TIMEFRAME_OPTIONS: {
      [QUERY_TIMEFRAME.ACTIVITY_DURING_TIMEFRAME]: {
        label: 'Activity This Period',
        tooltip: "Activity during the reporting period, regardless of the post's publish date",
      },
      [QUERY_TIMEFRAME.POSTS_PUBLISHED_IN_TIMEFRAME]: {
        label: 'Posts This Period',
        tooltip: 'Activity on posts published within the reporting period',
      },
    },
  },
  AD_ACCOUNTS_AND_CAMPAIGNS: {
    title: 'Ad Accounts and Campaigns',
    formKey: 'adAccountsAndCampaigns',
    AD_ACCOUNTS: {
      subHeader: 'Ad Accounts',
      hint: 'Selecting ad accounts with different currencies will limit your choice of metrics',
      placeholderText: 'Search ad accounts',
      tooltip: null,
      formKey: 'adAccounts',
    },
    CAMPAIGNS: {
      subHeader: 'Ad Campaigns',
      formKey: 'selectedAdCampaigns',
      TIKTOK_ADS_TABS: [
        {
          title: 'All Campaign Statuses',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.TIKTOK_ADS.value].ALL,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Active',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.TIKTOK_ADS.value].ENABLE,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Deleted',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.TIKTOK_ADS.value].DELETED,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Paused',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.TIKTOK_ADS.value].DISABLED,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Over Budget',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.TIKTOK_ADS.value].OVER_BUDGET,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
      ],
      META_ADS_TABS: [
        {
          title: 'All Campaign Statuses',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.META_ADS.value].ALL,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Active',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.META_ADS.value].ACTIVE,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Archived',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.META_ADS.value].ARCHIVED,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'Paused',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.META_ADS.value].PAUSED,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
        {
          title: 'With Issues',
          value: CAMPAIGN_STATUS_TABS[CHANNELS.META_ADS.value].WITH_ISSUES,
          subHeader: 'Ad Campaigns',
          placeholderText: 'Search campaigns',
          disabled: true,
        },
      ],
    },
  },
  CUSTOM_METRICS: {
    TITLE: 'Custom Metrics',
    CREATE_CUSTOM_METRIC: 'Create a Custom Metric',
    FORM_KEY: 'customMetric',
    TRY_CUSTOM_METRICS: 'Try Custom Metrics',
    LABEL: 'Custom Metrics',
    HINT: 'Metrics shown have been created by your team and can be managed in settings',
    PLACEHOLDER: 'Select a Metric',
    AGGREGATION: {
      TITLE: 'Aggregation - Advanced',
      OPTIONS: [
        {
          value: 'SUM',
          label: 'Post-Level Sum',
          tooltip:
            'The calculation is applied at the post level, and the values are summed across all posts in the reporting period.',
        },
        {
          value: 'AVERAGE',
          label: 'Post-Level Average',
          tooltip:
            'Calculation applied to each individual post.\n Summing those values (calculation results)\n\nDivide that total by the number of posts.',
        },
      ],
    },
  },
  MEDIA_TYPES: {
    subHeader: 'Select which media types to report on',
    title: 'Media Types',
    formKey: 'mediaTypes',
    placeholderText: {
      multiple: 'Search media types',
      single: 'Search media type',
    },
  },
  CONTENT_TAGS: {
    subHeader: 'Content Tags',
    title: 'Content Tags',
    formKey: 'contentTags',
    sectionDescription:
      'Selecting multiple channels will limit your choice to common metrics shared by the channels',
    placeholderText: 'Select Content Tags',
    linkText: 'Manage Content Tags',
    SELECT_FIELD: {
      subHeader: 'Do you want to report on all content or tagged content?',
      formKey: 'selectContentTags',
      SelectOptions: [
        { label: 'All Content', value: ALL_CONTENT },
        { label: 'Select Content Tags', value: SELECT_TAGS },
      ],
    },
  },
  SORT_ORDER: {
    title: 'Sort Order',
    subHeader: 'Select the Sort Order',
    formKey: 'sortOrder',
    SORT_ORDER_OPTIONS,
  },
  REPORT_VISUALIZATION: {
    title: 'Report Visualization',
    label: 'Select a Report Visualization',
    subHeader: 'Select Report Visualization',
    formKey: 'reportVisualization',
    placeholder: 'Select a Visualization',
    hint: 'Table breakdowns are determined by the metric you select',
  },
  REPORT_BREAKDOWN: {
    title: 'Report Breakdown',
    subHeader: 'Select a Report Breakdown',
    formKey: 'reportBreakdown',
  },
  REPORT_VISUALIZATION_BREAKDOWN: {
    title: 'Report Visualization',
    label: 'Select a Report Visualization',
    subHeader: 'Select Report Visualization',
    formKey: 'reportVisualization',
    placeholder: 'Select a Visualization',
    hint: 'Table breakdowns are determined by the metric you select',
  },
  DISPLAY_STRATEGY: {
    title: 'Display Values As',
    subHeader: 'Select how to display values in your report',
    formKey: 'displayStrategy',
    label: 'Select how to display values in your report',
    options: DISPLAY_STRATEGY_OPTIONS,
  },
});

export const REPORT_FORM_BANNER_TYPES = Object.freeze({
  PANEL: 'panel',
  SELECT_LIST: 'select-list',
});

export const REPORT_FORM_BANNER_PROPS = {
  // banner type is important for the banner to be displayed in the correct place
  // panel banner is displayed in the panel
  // select list banner is displayed in the select list
  // if the banner type is not provided, the banner will be displayed in the panel
  CREATOR_ACCESS_WARNING: {
    id: 'creator-access-warning',
    severity: 'warn',
    closable: true,
    message: REPORT_FORM_BANNER_MESSAGES.CREATOR_CHANNELS,
    type: REPORT_FORM_BANNER_TYPES.SELECT_LIST,
  },
  REQUEST_FAILED: {
    id: 'request-failed',
    severity: 'error',
    closable: false,
    message: REQUEST_FAILED_BANNER_MESSAGE,
    type: REPORT_FORM_BANNER_TYPES.PANEL,
  },
  DISCONTINUED_METRIC_WARNING: {
    id: 'discontinued-metric-warning',
    severity: 'warn',
    closable: false,
    message: DISCONTINUED_METRIC_WARNING_MESSAGE,
    type: REPORT_FORM_BANNER_TYPES.PANEL,
  },
  CREATOR_SOURCE_WARNING: {
    id: 'no-creators-warning',
    severity: 'info',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.CREATOR_SOURCE_WARNING,
    closable: true,
    callToAction: 'Manage Creators',
    disabled: true,
  },
  NOT_CONNECTED_CREATORS_WARNING: {
    id: 'not-connected-creators-warning',
    severity: 'warn',
    type: REPORT_FORM_BANNER_TYPES.SELECT_LIST,
    message: REPORT_FORM_BANNER_MESSAGES.NOT_CONNECTED_CREATORS_WARNING,
    closable: false,
    hideIcon: true,
  },
  SOME_CONNECTED_CREATORS_ONLY_WARNING: {
    id: 'some-connected-creators-only-warning',
    severity: 'warn',
    type: REPORT_FORM_BANNER_TYPES.SELECT_LIST,
    message: REPORT_FORM_BANNER_MESSAGES.SOME_CONNECTED_CREATORS_ONLY_WARNING,
    closable: false,
    hideIcon: true,
  },
  SOME_CREATORS_METRIC_SELECTION_WARNING: {
    id: 'some-creators-metric-selection-warning',
    severity: 'warn',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.SOME_CONNECTED_CREATORS_ONLY_WARNING,
    closable: false,
  },
  SOCIAL_LISTENING_CHANNELS_INFO: {
    id: 'social-listening-channels-info',
    severity: 'info',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.SOCIAL_LISTENING_CHANNELS_INFO,
    closable: false,
  },
  AD_ACCOUNT_INFO_TIKTOK: {
    id: 'ad-account-info-tiktok',
    severity: 'info',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.CONNECT_TIKTOK_ADS,
    closable: true,
    callToAction: 'Connect',
  },
  AD_ACCOUNT_INFO_META: {
    id: 'ad-account-info-meta',
    severity: 'info',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.CONNECT_META_ADS,
    closable: true,
    callToAction: 'Connect',
  },
  AD_ACCOUNT_RECONNECT_TIKTOK: {
    id: 'ad-account-reconnect-tiktok',
    severity: 'error',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.RECONNECT_TIKTOK_ADS,
    closable: false,
    callToAction: 'Connect',
  },
  AD_ACCOUNT_RECONNECT_META: {
    id: 'ad-account-reconnect-meta',
    severity: 'error',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.RECONNECT_META_ADS,
    closable: false,
    callToAction: 'Connect',
  },
  IG_LIVE_API_WARNING: {
    id: 'ig-live-api-warning',
    severity: 'warn',
    type: REPORT_FORM_BANNER_TYPES.PANEL,
    message: REPORT_FORM_BANNER_MESSAGES.IG_LIVE_API_WARNING,
    closable: true,
  },
};

export const PLATFORM_CUSTOM_METRIC_OPTIONS = [
  {
    text: 'Platform Metrics',
    value: 'platformMetrics',
    icon: 'system',
  },
  {
    text: 'Custom Metrics',
    value: 'customMetrics',
    icon: 'singleUser',
  },
];

export const REPORT_MENU_ITEMS = {
  EDIT: {
    label: 'Edit',
    value: 'REPORT.EDIT',
    icon: 'edit',
  },
  CUSTOMIZE_DATE: {
    label: 'Customize Date',
    value: 'REPORT.CUSTOMIZE_DATE',
    icon: 'calendar',
  },
  DUPLICATE: {
    label: 'Duplicate',
    value: 'REPORT.DUPLICATE',
    icon: 'duplicate',
  },
  DOWNLOAD_CSV: {
    label: 'Download CSV',
    value: 'REPORT.DOWNLOAD_CSV',
    icon: 'download',
  },
  DOWNLOAD_JPEG: {
    label: 'Download JPEG',
    value: 'REPORT.DOWNLOAD_JPEG',
    icon: 'download',
  },
  REMOVE_CUSTOM_DATE: {
    label: 'Remove Custom Date',
    value: 'REPORT.REMOVE_CUSTOM_DATE',
    icon: 'calendar-remove',
    class: 'dash-text-danger dash-fill-icon-danger',
    confirmDeleteTitle: 'Remove Custom Report Date?',
    confirmDeleteButtonLabel: 'Remove',
    confirmDeleteMessage:
      'This report will be reset and measured based on the Reporting Periods of the entire Dashboard. Do you want to proceed?',
    deleteSuccessMessage: 'Your custom date has been removed',
    deleteErrorMessage: 'Your custom date could not be removed. Please try again later.',
    acceptSeverity: 'danger',
  },
  DELETE: {
    label: 'Delete',
    value: 'REPORT.DELETE',
    icon: 'delete',
    class: 'dash-text-danger dash-fill-icon-danger',
    confirmDeleteTitle: 'Delete Report?',
    confirmDeleteButtonLabel: 'Delete',
    deleteSuccessMessage: 'Report deleted',
    confirmDeleteMessage: 'This will permanently delete your report.',
    deleteErrorMessage: 'Your report could not be deleted. Please try again later.',
    acceptSeverity: 'danger',
  },
};

export const REPORT_MENU = Object.freeze({
  HEADER: {
    EDIT: {
      label: 'Edit Header',
      icon: 'edit',
      value: 'HEADER',
      commandName: 'Save',
      maxLength: 100,
      roles: [ROLES.OWNER.value, ROLES.EDITOR.value],
      DIALOG: {
        toastSuccessMessage: 'Heading updated',
        toastErrorMessage: 'Heading could not be updated. Please try again later.',
      },
    },
    DELETE: {
      label: 'Delete Header',
      icon: 'delete',
      value: 'HEADER',
      commandName: 'Delete',
      roles: [ROLES.OWNER.value, ROLES.EDITOR.value],
      DIALOG: {
        confirmDeleteTitle: 'Delete Heading?',
        confirmDeleteMessage: 'This will permanently delete your heading.',
        confirmDeleteButtonLabel: 'Delete',
        toastSuccessMessage: 'Heading deleted',
        toastErrorMessage: 'Heading could not be deleted. Please try again later.',
      },
    },
  },
  NOTE: {
    EDIT: {
      label: 'Edit Note',
      icon: 'edit',
      value: 'NOTE',
      commandName: 'Save',
      maxLength: 248,
      roles: [ROLES.OWNER.value, ROLES.EDITOR.value],
      DIALOG: {
        toastSuccessMessage: 'Note updated',
        toastErrorMessage: 'Note could not be updated. Please try again later.',
      },
    },
    DELETE: {
      label: 'Delete Note',
      icon: 'delete',
      value: 'NOTE',
      commandName: 'Delete',
      roles: [ROLES.OWNER.value, ROLES.EDITOR.value],
      DIALOG: {
        confirmDeleteTitle: 'Delete Note?',
        confirmDeleteMessage: 'This will permanently delete your note.',
        confirmDeleteButtonLabel: 'Delete',
        toastSuccessMessage: 'Note deleted',
        toastErrorMessage: 'Note could not be deleted. Please try again later.',
      },
    },
  },
  REPORT: {
    ...REPORT_MENU_ITEMS,
  },
});

export const REPORT_META_FIELDS = Object.freeze({
  NAME: 'name',
  BRAND_IDS: 'brand_ids',
  COMPETITOR_SOURCE_ACCOUNT_IDS: 'competitor_source_account_ids',
  AGGREGATE_BY: 'aggregate_by',
  CONTENT_TAG_IDS: 'content_tag_ids',
  COMPETITOR_TAGS: 'competitor_tags',
  BRAND_TAGS: 'brand_tags',
  CHANNEL: 'channel',
  AGGREGATE: 'aggregate',
  METRIC: 'metric',
  METRIC_TYPE: 'metric_type',
});

export const AGGREGATION_OPTIONS = {
  SUM: 'SUM',
  AVERAGE: 'AVERAGE',
};

export const CREATOR_STATUS = {
  CONNECTED: 'Connected',
  NOT_CONNECTED: 'Not Connected',
  PENDING: 'Pending',
  NOT_INVITED: 'Not Invited',
};

export const CREATOR_INVITATION_STATUS_LABELS = {
  REVOKED: CREATOR_STATUS.NOT_CONNECTED,
  LAPSED: CREATOR_STATUS.NOT_CONNECTED,
  ACCEPTED: CREATOR_STATUS.CONNECTED,
  INVITATION_SENT: CREATOR_STATUS.PENDING,
  NOT_INVITED: CREATOR_STATUS.NOT_CONNECTED,
  default: CREATOR_STATUS.NOT_CONNECTED,
};

export const CREATOR_INVITATION_STATUS_COLORS = {
  REVOKED: 'neutral',
  LAPSED: 'neutral',
  ACCEPTED: 'success',
  INVITATION_SENT: 'warning',
  NOT_INVITED: 'neutral',
  default: 'neutral',
};

export const DISABLED_CREATOR_OPTION_TOOLTIP =
  'Creators must be in TikTok Creative Marketplace (TTCM) or be connected to have data available for reporting';

export const DASHBOARD_CREATOR_CHANNELS = Object.freeze(['instagram', 'tiktok']);

export const META_CHANNELS = Object.freeze([
  CHANNELS.INSTAGRAM.value,
  CHANNELS.FACEBOOK.value,
  CHANNELS.META_ADS.value,
]);

export const TIKTOK_CHANNELS = Object.freeze([CHANNELS.TIKTOK.value, CHANNELS.TIKTOK_ADS.value]);

export const STACKED_BAR_ACTIONS = Object.freeze({
  ENABLE: 'ENABLE_STACKED_BAR',
  DISABLE: 'DISABLE_STACKED_BAR',
});

export const INITIAL_REPORT_FORM_STATE = {
  adAccounts: [],
  adCampaigns: {
    selectedAdCampaigns: [],
    activeTab: CAMPAIGN_STATUS_TABS[CHANNELS.META_ADS.value].ACTIVE,
  },
  aggregate: AGGREGATION_OPTIONS.SUM,
  benchmarks: [],
  brands: {
    selectedBrands: [],
    selectedBrandTags: [],
  },
  channels: [],
  competitors: {
    activeTab: COMPETITOR_TABS.COMPETITORS,
    selectedCompetitors: [],
    selectedCompetitorTags: [],
  },
  community: {
    filters: {
      message_tag_ids: [],
      message_types: [],
      sentiment: [],
      assignee_ids: [],
    },
  },
  contentTags: [],
  contentTagSelectionMode: ALL_CONTENT,
  creators: {
    activeTab: CREATOR_TABS.CREATORS,
    selectedCreators: [],
    selectedCreatorTags: [],
  },
  customMetric: null,
  displayStrategy: null,
  earnedSourceType: UGC,
  goals: {
    overall: { value: null, type: null },
  },
  listening: {
    topic_ids: [],
    comparison_topic_ids: [],
    filters: {
      sentiment: [],
      media_types: [],
      locations: [],
      mention_types: [],
      competitors: [],
      industry_ids: [],
    },
  },
  mediaTypes: [],
  metrics: null,
  metricType: null,
  reportType: null,
  reportVisualization: null,
  reportBreakdown: null,
  secondaryAggregateBy: null,
  sortOrder: null,
  staticDates: null,
};
